/**
 * 🏠 **Which part of the storehouse a triangle belongs to** — pure arithmetic,
 * no `three`, so it tests without a browser and the design tool can draw it.
 *
 * **D109, T135.** 🎯 **haugur, 2026-09-03, having placed the first one:**
 * *"door is both on front and back, wich is wrong, the door could be in a
 * little different colour that the gable, just to make it visible."*
 *
 * ## Why a classifier and not a better model
 *
 * The model was made from ONE photograph, so its back is the model's guess —
 * and it guessed a mirror of the front, door and all. Regenerating from four
 * views would need four consistent pictures that do not exist. **Deciding per
 * triangle what it is, and dressing each kind differently, is deterministic,
 * cheap, and stays on the proven material path.**
 *
 * ## The three kinds
 *
 * | | | dressed as |
 * |---|---|---|
 * | `door` | Front face, middle, below head height | the model's own texture, **darkened** |
 * | `backGable` | Back face, not roof | **the game's own driftwood-plank tile**, tiled per metre — plain planks, no door |
 * | `body` | Everything else | the model's own texture |
 *
 * ## The frame
 *
 * Coordinates are **normalised into the model's own box**: `0…1` along each
 * axis, with the gable that has the door at **z = 0** (the model's −z end —
 * read off `Design/samples/70_storehouse_model.png`, the frame taken from −z).
 * The normal is the triangle's own, unit length, in the model's axes.
 *
 * ⚠️ **The numbers below were tuned on THIS model** (`70_storehouse_model.glb`)
 * by looking at `tools/design-storehouse-model.mjs --view`. A regenerated
 * model wants them looked at again, not trusted.
 */

/**
 * 🔦 **The patch of wall one torch is nailed to**, in metres, relative to the
 * floor of the torch's own cell. **Issue #532.**
 *
 * A torch's post is `POST_THICKNESS` — 0.125 m — square, and it stands from
 * 0.2 m above the cell floor to about 0.78 m. ⚠️ **The band is widened a little
 * at both ends on purpose**: a patch exactly the size of the post can fall
 * between two vertices of a photographed mesh and find nothing at all, and
 * "no wall here" is a much worse answer than "the wall is roughly there."
 *
 * 🔴 **This is the board's own lesson applied to a smaller object**: measure the
 * wall **under the thing being hung**, not the model, not its box, and not its
 * most extreme vertex.
 */
export const TORCH_PATCH = { halfAlong: 0.1, low: 0.15, high: 0.85 } as const;

export type BurFaceKind = 'body' | 'backGable' | 'door';

export interface BurFaceSample {
  /** Centroid, normalised into the model's box: 0…1 on each axis. */
  readonly cx: number;
  readonly cy: number;
  readonly cz: number;
  /** Unit normal in the model's axes. */
  readonly nx: number;
  readonly ny: number;
  readonly nz: number;
}

/** How far in from an end a face still counts as that end's gable, 0…1 of the length. */
export const GABLE_DEPTH = 0.08;
/** The door: half its width, 0…1 of the model's width; and its top, 0…1 of the height. */
export const DOOR_HALF_WIDTH = 0.14;
export const DOOR_TOP = 0.48;

export function classifyBurFace(f: BurFaceSample): BurFaceKind {
  /* A roof face leans up; a gable face stands. Turf overhanging a gable end
     leans mostly up too, which keeps it turf. */
  const stands = Math.abs(f.ny) < 0.5;
  if (stands && f.nz > 0.5 && f.cz > 1 - GABLE_DEPTH) return 'backGable';
  if (
    stands &&
    f.nz < -0.5 &&
    f.cz < GABLE_DEPTH &&
    Math.abs(f.cx - 0.5) < DOOR_HALF_WIDTH &&
    f.cy < DOOR_TOP
  )
    return 'door';
  return 'body';
}

/**
 * 🪧 **The frontmost point of the wall inside one small patch of the gable** —
 * in metres, in the house's own frame, or `undefined` if the patch is empty.
 *
 * 🎯 **haugur, 2026-09-07, of the carved boards:** they *"stand out from the
 * wall."* 🔴 **They stood 41 cm proud of it**, on a gable whose entire surface
 * relief is 10 cm.
 *
 * ## 🔴 Why the DOOR could not answer this, though it looked as if it could
 *
 * The board used to hang 1 cm proud of `Box3.min.z` of the door's triangles —
 * *"the door is by definition on the wall"*, which is **true of the door's
 * surface and false of its bounding box.** ⚠️ **A box keeps the single most
 * extreme vertex it was given.** Measured off `bur.glb`: three quarters of the
 * door's own vertices lie between −4.04 and −4.00 m, and a **5% lip reaches
 * −4.44** — so the box answered −4.44 and the board hung out there on its own.
 *
 * ⚠️ **The fix before this one moved the number six centimetres and the fault
 * was forty-six**, because it swapped one extreme for another extreme.
 *
 * ✅ **So ask the wall the board actually touches**, over the board's own
 * rectangle, and take the frontmost vertex **there**. A bump under the board is
 * a thing the board must clear; a lip half a metre away is not. **This cannot
 * be wrong by an amount unrelated to what the board is nailed to**, which is
 * the one failure this board has had four times.
 *
 * @param points Vertex positions, `x, y, z` repeating, **already in the
 *   house's metre frame**. ⚠️ **Say which frame, every time** — a number in
 *   metres and a number in model units look identical, and only a photograph
 *   has ever told them apart here.
 * @param centreX Middle of the patch, across the gable.
 * @param centreY Middle of the patch, above the floor.
 * @param halfWidth Half the patch's width.
 * @param halfHeight Half its height.
 * @param frontOf Only points at or in front of this z count. ⚠️ **The house has
 *   two gables and the far one is 9 m away**; without this the back wall wins,
 *   and it does not face the same way. ⚠️ **The mesh's winding is NOT
 *   consistent** — it is photogrammetry — so a triangle's normal cannot be used
 *   to tell the two ends apart. **Position can.**
 */
export function frontmostZInPatch(
  points: ArrayLike<number>,
  centreX: number,
  centreY: number,
  halfWidth: number,
  halfHeight: number,
  frontOf: number,
): number | undefined {
  return nearestSurfaceInPatch(
    points,
    2,
    -1,
    { x: centreX, y: centreY, z: 0 },
    { x: halfWidth, y: halfHeight, z: 0 },
    frontOf,
  );
}

/**
 * 📏 **The same measurement, on whichever wall you are standing in front of.**
 *
 * 🔦 **Added 2026-09-09 for issue #532** — a torch on a house stood **46 cm off
 * it and cut through the door**, which is {@link frontmostZInPatch}'s own bug
 * one object smaller. 🔴 **The house has four walls and they are NOT inset by
 * the same amount**: the gables sit about **0.43 m** inside their block faces
 * and the long sides only **0.22 m**, so a single number is a fifth of a metre
 * wrong on one of them.
 *
 * ⚠️ **So this is not a new instrument.** {@link frontmostZInPatch} is now a
 * three-line call into it, and the sign's behaviour is unchanged — **there is
 * one loop, not two that drift.**
 *
 * @param points Vertex positions, `x, y, z` repeating, **already in the house's
 *   metre frame.** ⚠️ **Say which frame, every time.**
 * @param axis Which axis the wall faces along: `0` for x, `1` for y, `2` for z.
 * @param outward `-1` if the surface faces the negative end of that axis, `+1`
 *   for the positive end. **A gable and the side wall beside it face different
 *   ways**, and nothing in the mesh says which — the winding is photogrammetry
 *   and cannot be trusted, so the caller states it.
 * @param centre Middle of the patch. **The component for `axis` is ignored**,
 *   because that is the number being measured.
 * @param half Half the patch's size, same convention.
 * @param beyond Only points on the outward side of this count. ⚠️ **A house has
 *   two of every wall and the far one is up to 9 m away**; without this the
 *   wrong one wins and it does not face the same way.
 */
export function nearestSurfaceInPatch(
  points: ArrayLike<number>,
  axis: 0 | 1 | 2,
  outward: -1 | 1,
  centre: { readonly x: number; readonly y: number; readonly z: number },
  half: { readonly x: number; readonly y: number; readonly z: number },
  beyond: number,
): number | undefined {
  const c = [centre.x, centre.y, centre.z];
  const h = [half.x, half.y, half.z];
  let best: number | undefined;
  for (let i = 0; i + 2 < points.length; i += 3) {
    /* eslint-disable security/detect-object-injection -- three reads at an
       arithmetic offset into a vertex array, as `sampleTriangle` does, and two
       into three-element arrays this function just built. */
    const p = [points[i] ?? 0, points[i + 1] ?? 0, points[i + 2] ?? 0];
    const here = p[axis] ?? 0;
    if (outward < 0 ? here > beyond : here < beyond) continue;
    let inside = true;
    for (const other of [0, 1, 2]) {
      if (other === axis) continue;
      if (Math.abs((p[other] ?? 0) - (c[other] ?? 0)) > (h[other] ?? 0)) inside = false;
    }
    /* eslint-enable security/detect-object-injection */
    if (!inside) continue;
    if (best === undefined || (outward < 0 ? here < best : here > best)) best = here;
  }
  return best;
}

/**
 * The centroid and unit normal of one triangle, from nine numbers, normalised
 * into a box. Shared by the game and the design tool so the two cannot drift.
 */
export function sampleTriangle(
  p: ArrayLike<number>,
  i: number,
  min: { readonly x: number; readonly y: number; readonly z: number },
  size: { readonly x: number; readonly y: number; readonly z: number },
): BurFaceSample {
  /* eslint-disable security/detect-object-injection -- nine reads at an offset
     into a vertex array; the index is arithmetic, never a name from outside. */
  const ax = p[i] ?? 0;
  const ay = p[i + 1] ?? 0;
  const az = p[i + 2] ?? 0;
  const bx = p[i + 3] ?? 0;
  const by = p[i + 4] ?? 0;
  const bz = p[i + 5] ?? 0;
  const cx = p[i + 6] ?? 0;
  const cy = p[i + 7] ?? 0;
  const cz = p[i + 8] ?? 0;
  /* eslint-enable security/detect-object-injection */
  const ux = bx - ax;
  const uy = by - ay;
  const uz = bz - az;
  const vx = cx - ax;
  const vy = cy - ay;
  const vz = cz - az;
  let nx = uy * vz - uz * vy;
  let ny = uz * vx - ux * vz;
  let nz = ux * vy - uy * vx;
  const len = Math.hypot(nx, ny, nz) || 1;
  nx /= len;
  ny /= len;
  nz /= len;
  const norm = (v: number, lo: number, s: number): number => (s > 0 ? (v - lo) / s : 0);
  return {
    cx: norm((ax + bx + cx) / 3, min.x, size.x),
    cy: norm((ay + by + cy) / 3, min.y, size.y),
    cz: norm((az + bz + cz) / 3, min.z, size.z),
    nx,
    ny,
    nz,
  };
}
